import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import { _notNil } from '@/littledash';
import { type Animal } from '@/model/Animal.model';
import type { Cage, CageApiId } from '@/model/Cage.model';
import { ApiService } from '@/support/ApiService';
import { useFetchCollection } from '@/support/Hooks/fetch';
import { modalAction } from '@/utils/modal';
import { type FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MAX_CAGE_POPULATION } from '@/constants/cage';

interface UndoEuthaniseProps {
  subjects: Array<Animal>;
  handleCallback: (reasonForChange: string) => void;
}

const UndoEuthanise: FC<UndoEuthaniseProps> = ({ subjects, handleCallback }) => {
  const maxSubjects = MAX_CAGE_POPULATION;
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { closeModal } = modalAction(dispatch);

  const { id } = useParams();
  const {
    collection: collections,
    collectionLoading,
    collectionError,
  } = useFetchCollection<Cage>({
    collectionType: 'studyCages',
    params: { studyId: id },
    includes: 'animals_count',
    queryParams: {
      perPage: -1,
      sort: 'cage_order',
    },
  });

  let what, subjectsToMove;
  if (subjects.length > 1) {
    subjectsToMove = subjects.length;
    what = `these ${subjectsToMove} animals`;
  } else {
    what = 'this animal';
    subjectsToMove = 1;
  }

  const submitUnmarkAsDeceased = async (cageApiId: CageApiId) => {
    const animalApiIds = subjects.map((s) => s.api_id).filter(_notNil);
    if (animalApiIds.length === 0) {
      return;
    }

    setLoading(true);
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/animals/undo-terminate',
      body: {
        animal_api_ids: animalApiIds,
        cage_api_id: cageApiId,
      },
    });
    if (response.type === 'error') {
      setLoading(false);
      setError(true);
      return;
    }
    handleCallback('Successfully moved and unmarked as deceased');
    setLoading(false);
  };

  const onClick = (cageApiId: CageApiId) => {
    submitUnmarkAsDeceased(cageApiId);
  };

  return collectionLoading ? (
    <div className="w-100 h-100">
      <Loading />
    </div>
  ) : (
    <form className={`ui__narrow-modal ${loading && 'disabled-form'}`}>
      <div className="pb3 tc">
        <h3 className="normal f4 pb2">Undo euthanisation for {what}?</h3>
        <p>Please select a cage - each cage can hold up to {maxSubjects} animals</p>
      </div>
      {collectionError && <ApiErrorBanner errorType={ApiErrorType.FETCH} error={collectionError} />}
      {error && <ApiErrorBanner error={error} />}
      <ul className="ui-card-list touch overflow-y-scroll group-list">
        {collections?.map((collection) => {
          const isCurrentCollection = !Array.isArray(subjects);
          return (collection.animals_count as number) + subjectsToMove > maxSubjects ? (
            <FullCollection subjectCount={collection.animals_count ?? 0} key={collection.id} name={collection.name} />
          ) : (
            <AvailableCollection
              collection={collection}
              isCurrentCollection={isCurrentCollection}
              subjectCount={collection.animals_count ?? 0}
              key={collection.id}
              onClick={onClick}
            />
          );
        })}
      </ul>
      <div className="pt2 bt b--silver">
        <a className={`button plain db w-100 mt2 ${loading && 'disabled-link'}`} onClick={() => closeModal()}>
          Cancel
        </a>
      </div>
    </form>
  );
};

const FullCollection: FC<{ name: string; subjectCount: number }> = ({ name, subjectCount }) => (
  <li>
    <a className="disabled-link">
      <div className="flex justify-between">
        <span>{name}</span>
        <span className="red">{subjectCount}</span>
      </div>
    </a>
  </li>
);

const AvailableCollection: FC<{
  collection: Cage;
  isCurrentCollection: boolean;
  subjectCount: number;
  onClick: (cageApiId: CageApiId) => void;
}> = ({ collection, isCurrentCollection, subjectCount, onClick }) => (
  <li>
    <a onClick={() => onClick(collection.api_id)} className={`${isCurrentCollection && 'disabled-link active-box'}`}>
      <div className="flex justify-between">
        <span>{collection.name}</span>
        <span className="mid-gray">{subjectCount}</span>
      </div>
    </a>
  </li>
);

export default UndoEuthanise;
